import { NetworkStatus } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteLoader from "react-window-infinite-loader";

import usePolledQuery from "apollo/usePolledQuery";
import useBulkActionsSelection from "components/BulkActions/useBulkActionsSelection";
import { useBulkActionsShiftSelection } from "components/BulkActions/useBulkActionsShiftSelection";
import NotFoundPage from "components/error/NotFoundPage";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import { SavedFilterView } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import ListEntitiesNew from "components/ListEntitiesNew";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import ViewCustomizationContextProvider from "components/ViewCustomization/Context";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchModulesOutput } from "types/generated";
import { uniqByKey } from "utils/uniq";

import { PollingIntervalGroups } from "../../../apollo/constants";
import ModulesBulkActions from "./components/BulkActions";
import ModulesEmptyState from "./components/EmptyState";
import FiltersLayout from "./components/FiltersLayout";
import ModuleVirtualizedListItem from "./components/ListItem/Virtualized";
import ModulesPageLayout from "./components/PageLayout";
import {
  INITIAL_MODULE_LIST_VIEW_ITEMS,
  initialSortDirection,
  initialSortOption,
  MODULE_LIST_VIEW_ITEMS_SETTINGS_KEY,
  MODULES_LIMIT,
} from "./constants";
import { SEARCH_MODULES } from "./gql";
import { getAllItemsForSelectAll } from "./helpers";

const Modules = () => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    allSelected,
    selectedSet,
    syncAllSelectedItems,
    selectItem,
    unselectItem,
    onBulkSelectAll,
    onBulkResetAll,
    syncSelectedItemsWithVisibleOnes,
    onBulkContinueWith,
  } = useBulkActionsSelection();

  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, initialSortOption, initialSortDirection),
    [urlParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams);

    return [...(predicatesMap?.values() || [])];
  }, [urlParams]);

  const { onError } = useTypedContext(FlashContext);

  const {
    error,
    loading,
    data,
    fetchMore: fetchMoreModules,
    refetch,
    networkStatus,
    previousData,
  } = usePolledQuery<{
    searchModules: SearchModulesOutput;
  }>(SEARCH_MODULES, {
    variables: {
      input: {
        first: MODULES_LIMIT,
        after: null,
        fullTextSearch: searchInput,
        predicates,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    nextFetchPolicy: "cache-first",
  });

  const initialLoading = loading && networkStatus === NetworkStatus.loading;

  const memoizedModules = useMemo(
    () =>
      data?.searchModules?.edges.map((edge) => edge.node) ||
      previousData?.searchModules?.edges.map((edge) => edge.node) ||
      [],
    [data?.searchModules?.edges, previousData?.searchModules?.edges]
  );

  const memoizedModulesMap = useMemo(
    () => new Map(memoizedModules.map((edge) => [edge.id, edge])),
    [memoizedModules]
  );

  const modulesQueryRefetch = async () => {
    try {
      await refetch();
    } catch (e) {
      onError(e);
    }
  };

  // mark selected new loaded modules if allSelected is true
  useEffect(() => {
    if (allSelected) {
      syncAllSelectedItems(getAllItemsForSelectAll(memoizedModules));
    }
  }, [allSelected, memoizedModules, syncAllSelectedItems]);

  // sync the selected items with the visible items on the list (filter out the ones that are not visible, for example in result of filters changing)
  useEffect(() => {
    if (selectedSet.size) {
      syncSelectedItemsWithVisibleOnes(memoizedModulesMap);
    }
  }, [memoizedModulesMap, selectedSet.size, syncSelectedItemsWithVisibleOnes]);

  const handleBulkSelectAll = useCallback(() => {
    onBulkSelectAll(getAllItemsForSelectAll(memoizedModules));
  }, [memoizedModules, onBulkSelectAll]);

  const isItemLoaded = (value: number) => value < memoizedModules.length;

  const loadMoreItems = async () => {
    try {
      if (data?.searchModules?.pageInfo.endCursor && data?.searchModules?.pageInfo.hasNextPage) {
        await fetchMoreModules({
          updateQuery: (prev, { fetchMoreResult }) => {
            if (fetchMoreResult && fetchMoreResult.searchModules.edges.length > 0) {
              return {
                ...fetchMoreResult,
                searchModules: {
                  ...fetchMoreResult.searchModules,
                  edges: uniqByKey(
                    [...(prev.searchModules.edges || []), ...fetchMoreResult.searchModules.edges],
                    "cursor"
                  ),
                },
              };
            }

            return prev;
          },
          variables: {
            input: {
              first: MODULES_LIMIT,
              after: data.searchModules.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates,
              ...(sortOptionFields && { orderBy: sortOptionFields }),
            },
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  };

  const onSelection = useBulkActionsShiftSelection({
    collection: memoizedModules,
    selectItem,
    unselectItem,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading && !data?.searchModules) {
    return (
      <ModulesPageLayout>
        <PageLayoutSkeleton />
      </ModulesPageLayout>
    );
  }

  if (!loading && !data?.searchModules) {
    return <NotFoundPage />;
  }

  const handleBulkActionsFinish = async () => {
    await modulesQueryRefetch();
  };

  return (
    <ModulesPageLayout>
      <ViewCustomizationContextProvider
        localStorageKey={MODULE_LIST_VIEW_ITEMS_SETTINGS_KEY}
        initialItems={INITIAL_MODULE_LIST_VIEW_ITEMS}
      >
        <FiltersLayout
          predicates={predicates}
          allSelected={allSelected}
          onSelectAll={handleBulkSelectAll}
          onResetAll={onBulkResetAll}
          hasItems={memoizedModules.length > 0}
          currentSavedView={currentSavedView}
          setCurrentSavedView={setCurrentSavedView}
        >
          {data && !memoizedModules.length && (
            <ModulesEmptyState didPerformSearch={!!searchInput || predicates.length > 0} />
          )}

          {data && memoizedModules.length > 0 && (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={memoizedModules.length + MODULES_LIMIT}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered }) => (
                <ListEntitiesNew
                  itemCount={memoizedModules.length}
                  itemProps={{
                    items: memoizedModules,
                    onCheckItem: onSelection,
                    selectedSet,
                  }}
                  virtualizedItem={ModuleVirtualizedListItem}
                  itemKey={(index) => memoizedModules[index].id}
                  onItemsRendered={onItemsRendered}
                  outerContainerRef={virtualizedListContainerRef}
                />
              )}
            </InfiniteLoader>
          )}

          <ModulesBulkActions
            virtualizedListContainerRef={virtualizedListContainerRef}
            selectedSet={selectedSet}
            modulesMap={memoizedModulesMap}
            onBulkResetAll={onBulkResetAll}
            onFinish={handleBulkActionsFinish}
            onItemDismiss={unselectItem}
            onBulkContinueWith={onBulkContinueWith}
          />
        </FiltersLayout>
      </ViewCustomizationContextProvider>
    </ModulesPageLayout>
  );
};

export default Modules;
